import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { changePassword } from '../lib/api';
import { EncryptFE } from '../lib/encrypt';
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from 'react-router-dom';

const ChangePassword = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false
  });
  const [errors, setErrors] = useState({});
  const [userInfo, setUserInfo] = useState({
    email: '',
    oldPassword: '••••••••'
  });
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    if (!token || !user || !user.EmailID) {
      navigate('/sign-in');
      return;
    }

    if (!user.IsPasswordAutoGenerated) {
      navigate('/');
      return;
    }

    setUserInfo({
      email: user.EmailID || '',
      oldPassword: '••••••••'
    });
  }, [navigate]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const handleTogglePassword = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain uppercase, lowercase, number and special character';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      if (!user || !user.EmailID) {
        setErrors({ submit: 'User information not found. Please login again.' });
        return;
      }

      const actualOldPassword = user.TemporaryPassword || '';

      await changePassword(
        EncryptFE(userInfo.email),
        EncryptFE(actualOldPassword),
        EncryptFE(formData.newPassword)
      );

      const updatedUser = {
        ...user,
        IsPasswordAutoGenerated: false
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      setSuccessMessage('Password changed successfully! Redirecting to dashboard...');

      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Password change error:', error);

      let errorMessage = 'Failed to change password';

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', width: '0%', variant: 'danger' };

    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[@$!%*?&]/.test(password)) strength += 1;

    const strengthMap = {
      1: { label: 'Very Weak', width: '20%', variant: 'danger' },
      2: { label: 'Weak', width: '40%', variant: 'warning' },
      3: { label: 'Fair', width: '60%', variant: 'info' },
      4: { label: 'Good', width: '80%', variant: 'primary' },
      5: { label: 'Strong', width: '100%', variant: 'success' }
    };

    return strengthMap[strength] || { label: '', width: '0%', variant: 'secondary' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <section className='auth bg-base d-flex flex-wrap justify-content-center align-items-center'>
      <div className='auth-right py-10 px-24 d-flex flex-column justify-content-center'>
        <div className='max-w-464-px mx-auto w-100'>
          <div>
            <Link to='/' className='mb-16 max-w-290-px d-block mx-auto text-center'>
              <img src='assets/images/SoksanLogo.png' alt='' className="mx-auto d-block" />
            </Link>
            <h4 className='mb-10'>Set New Password</h4>
            <p className='mb-12 text-secondary-light text-lg'>
              Your password was auto-generated. Please set a new password to continue.
            </p>
          </div>

          {successMessage && (
            <div
              className="mb-24 p-12 radius-8 d-flex align-items-center gap-2"
              style={{ backgroundColor: "#f6ffed", border: "1px solid #b7eb8f", color: "#52c41a" }}
            >
              <Icon icon="solar:check-circle-bold" fontSize={20} />
              <span className="text-sm fw-medium">{successMessage}</span>
            </div>
          )}

          {errors.submit && (
            <div
              className="mb-24 p-12 radius-8 d-flex align-items-center gap-2"
              style={{ backgroundColor: "#fff1f0", border: "1px solid #ffa39e", color: "#cf1322" }}
            >
              <Icon icon="solar:danger-circle-bold" fontSize={20} />
              <span className="text-sm fw-medium">{errors.submit}</span>
            </div>
          )}

          <form action='#' onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className='mb-16'>
              <div className='icon-field'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='mage:email' />
                </span>
                <input
                  type='email'
                  className='form-control h-56-px bg-neutral-50 radius-12'
                  placeholder='Email'
                  value={userInfo.email}
                  disabled
                />
              </div>
              <div className="form-text text-secondary-light text-sm mt-4">
                Your registered email address
              </div>
            </div>

            {/* Current Password Field */}
            <div className='mb-16'>
              <div className='icon-field'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='solar:lock-password-outline' />
                </span>
                <input
                  type='password'
                  className='form-control h-56-px bg-neutral-50 radius-12'
                  placeholder='Current Password'
                  value={userInfo.oldPassword}
                  disabled
                />
              </div>
              <div className="form-text text-secondary-light text-sm mt-4">
                System generated password
              </div>
            </div>


            {/* New Password Field */}
            <div className='mb-16'>
              <div className='icon-field'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='solar:lock-password-outline' />
                </span>
                <input
                  type={showPassword.newPassword ? 'text' : 'password'}
                  className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.newPassword ? 'border-danger' : ''}`}
                  placeholder='New Password'
                  value={formData.newPassword}
                  onChange={handleInputChange('newPassword')}
                  disabled={loading}
                />
                <span
                  onClick={() => handleTogglePassword('newPassword')}
                  className={`toggle-password ${showPassword.newPassword ? 'ri-eye-line' : 'ri-eye-off-line'} cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                />
              </div>
              {errors.newPassword && (
                <div className="text-danger text-sm mt-4">
                  <Icon icon="solar:danger-circle-bold" className="me-1" />
                  {errors.newPassword}
                </div>
              )}

              {formData.newPassword && (
                <div className="mt-12">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <small className="text-secondary-light">Password strength:</small>
                    <small className={`fw-semibold text-${passwordStrength.variant}`}>
                      {passwordStrength.label}
                    </small>
                  </div>
                  <div className="progress" style={{ height: '6px', borderRadius: '3px' }}>
                    <div
                      className={`progress-bar bg-${passwordStrength.variant}`}
                      style={{ width: passwordStrength.width, borderRadius: '3px' }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="form-text text-secondary-light text-sm mt-4">
                Minimum 8 characters with uppercase, lowercase, number and special character
              </div>
            </div>

            {/* Confirm Password Field */}
            <div className='mb-24'>
              <div className='icon-field'>
                <span className='icon top-50 translate-middle-y'>
                  <Icon icon='solar:lock-password-outline' />
                </span>
                <input
                  type={showPassword.confirmPassword ? 'text' : 'password'}
                  className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.confirmPassword ? 'border-danger' : ''}`}
                  placeholder='Confirm New Password'
                  value={formData.confirmPassword}
                  onChange={handleInputChange('confirmPassword')}
                  disabled={loading}
                />
                <span
                  onClick={() => handleTogglePassword('confirmPassword')}
                  className={`toggle-password ${showPassword.confirmPassword ? 'ri-eye-line' : 'ri-eye-off-line'} cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light`}
                />
              </div>
              {errors.confirmPassword && (
                <div className="text-danger text-sm mt-4">
                  <Icon icon="solar:danger-circle-bold" className="me-1" />
                  {errors.confirmPassword}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type='submit'
              disabled={loading}
              className='btn text-sm btn-sm px-12 py-16 w-100 radius-12'
              style={{ backgroundColor: "#FAA61A", borderColor: "#FAA61A", color: "#fff" }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Setting Password...
                </>
              ) : (
                'Set New Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ChangePassword;